import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Chip, Grid, IconButton } from "@mui/material";
import axios from "axios";
import styles from "./ActivityTabStyles";
import AddActivityModal from "../AddActivityModal/AddActivityModal";
import QuestionnaireModal from "../QuestionnaireTab/QuestionnaireModal";
import { format, set } from "date-fns";
import {
  PhoneCallback,
  NoteAlt,
  QuestionAnswer,
  Chat,
  VideoCall,
  Email,
  Sms,
  Assignment,
} from "@mui/icons-material";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

interface Activity {
  id: number;

  activity_type: string;
  title: string;
  date: string;
  transcript: string | null;
  recording: string | null;
  questionnaires_id: number | null;
  has_questionnaire_results: boolean;
  questionnaire_status?: "pending" | "completed";
  response_id: string | null;
  notes: string | null;
}

interface ActivityTabProps {
  memberId: string;
  refreshMembers: () => void;
}

const getActivityIcon = (activity: Activity) => {
  if (!activity.activity_type) return <NoteAlt />;

  switch (activity.activity_type.toLowerCase()) {
    case "call":
      return <PhoneCallback />;
    case "chat":
      return <Chat />;
    case "video":
      return <VideoCall />;
    case "email":
      return <Email />;
    case "sms":
      return <Sms />;
    case "questionnaire":
      return <Assignment />;
    default:
      return <NoteAlt />;
  }
};

const ActivityTab: React.FC<ActivityTabProps> = ({
  memberId,
  refreshMembers,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] =
    useState(false);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    number | null
  >(null);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [isViewOnly, setIsViewOnly] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, [memberId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/activities`
      );
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchActivities();
  };

  const handleActivityAdded = (newActivity: Activity) => {
    setActivities((prevActivities) => [newActivity, ...prevActivities]);
  };

  const handleOpenQuestionnaireModal = useCallback(
    (questionnaireId: number, responseId: string) => {
      setSelectedQuestionnaireId(questionnaireId);
      setSelectedResponseId(responseId);
      setIsQuestionnaireModalOpen(true);
    },
    []
  );

  const handleCloseQuestionnaireModal = useCallback(() => {
    setIsQuestionnaireModalOpen(false);
    setSelectedQuestionnaireId(null);
    setSelectedResponseId(null);
    setIsViewOnly(false);
    fetchActivities();
  }, []);

  if (loading) return <LoadingSpinner />;
  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h6">Participant Activity</Typography>
        <Button
          variant="contained"
          onClick={handleOpenModal}
          sx={styles.addButton}
        >
          Add New Activity Record
        </Button>
      </Box>
      <Grid container spacing={2}>
        {activities.map((activity) => (
          <Grid item xs={12} key={activity.id}>
            <Box sx={styles.activityRow}>
              <Box sx={styles.activityHeader}>
                <IconButton sx={styles.iconButton}>
                  {getActivityIcon(activity)}
                </IconButton>
                <Typography sx={styles.title}>{activity.title}</Typography>
                <Typography sx={styles.date}>
                  {format(new Date(activity.date), "MMM d, yyyy h:mm a")}
                </Typography>
              </Box>
              {activity.notes && (
                <Box sx={styles.notesContainer}>
                  <NoteAlt sx={styles.icon} />
                  <Typography sx={styles.notes}>{activity.notes}</Typography>
                </Box>
              )}
              <Box sx={styles.actionsContainer}>
                {activity.transcript && (
                  <Chip
                    icon={<NoteAlt />}
                    label="View Transcript"
                    onClick={() => console.log("View transcript")}
                    sx={styles.actionChip}
                  />
                )}
                {activity.recording && (
                  <Chip
                    icon={<PhoneCallback />}
                    label="Play Recording"
                    onClick={() => console.log("Play recording")}
                    sx={styles.actionChip}
                  />
                )}
                {activity.questionnaires_id && activity.response_id && (
                  <Chip
                    icon={<QuestionAnswer />}
                    label="View Responses"
                    onClick={() => {
                      setIsViewOnly(true);
                      handleOpenQuestionnaireModal(
                        activity.questionnaires_id!,
                        activity.response_id!
                      );
                    }}
                    sx={styles.actionChip}
                  />
                )}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <AddActivityModal
        open={isModalOpen}
        onClose={handleCloseModal}
        memberId={memberId}
        onActivityAdded={handleActivityAdded}
        refreshMembers={refreshMembers}
      />
      {isQuestionnaireModalOpen &&
        selectedQuestionnaireId &&
        selectedResponseId && (
          <QuestionnaireModal
            open={isQuestionnaireModalOpen}
            onClose={handleCloseQuestionnaireModal}
            memberId={memberId}
            questionnaireId={selectedQuestionnaireId}
            responseId={selectedResponseId}
            refreshMembers={refreshMembers}
            isViewOnly={isViewOnly}
          />
        )}
    </Box>
  );
};

export default ActivityTab;
